import { useEffect, useState } from 'react'
import { Accordion, Button } from '@heroui/react'
import type { StaffKey } from '../lib/staffText'
import { Icon, type IconName } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { Find } from './settingsKit'
import '../styles/settings.css'

export interface MenuEntry {
  key: string
  label: StaffKey
  icon: IconName
  /** A sub-heading inside the area (Online booking, Integrations). */
  part?: StaffKey
  /** A page of its own outside Settings. */
  page?: boolean
}

export interface MenuGroup {
  key: string
  label: StaffKey
  entries: MenuEntry[]
}

export interface FoundRow {
  entry: MenuEntry
  group: MenuGroup
}

/**
 * Settings' left menu: one collapsible section per area (HeroUI Accordion), the open page's
 * area open to begin with; part names as small headings inside. A search lists matches instead.
 */
export function SettingsMenu({
  groups,
  currentKey,
  filter,
  onFilter,
  found,
  label,
  word,
  onOpen,
}: {
  groups: MenuGroup[]
  currentKey: string
  filter: string
  onFilter: (q: string) => void
  found: FoundRow[]
  label: string
  /** Words for labels: the installation owner's pages stay English. */
  word: (k: StaffKey) => string
  onOpen: (entry: MenuEntry) => void
}) {
  const { say } = useStaffText()
  const q = filter.trim()
  const areaOf = groups.find((g) => g.entries.some((e) => !e.page && e.key === currentKey))?.key
  const [open, setOpen] = useState<Set<string | number>>(() => new Set(areaOf ? [areaOf] : groups.slice(0, 1).map((g) => g.key)))
  // Arriving at a page of a closed area opens it.
  useEffect(() => {
    if (areaOf) setOpen((o) => (o.has(areaOf) ? o : new Set([...o, areaOf])))
  }, [areaOf])

  return (
    <nav className="set-menu" aria-label={label}>
      <Find className="set-menu__find" value={filter} onChange={onFilter} onSubmit={() => found[0] && onOpen(found[0].entry)} label={say('admin.find')} />
      {q ? (
        found.length > 0 ? (
          <ul className="set-menu__found">
            {found.map(({ entry, group }) => (
              <li key={entry.key}>
                <Button variant="ghost" size="sm" className="set-menu__hit" onPress={() => onOpen(entry)}>
                  <Icon name={entry.icon} size={16} />
                  <span>
                    {word(entry.label)}
                    <small>{word(group.label)}</small>
                  </span>
                  {entry.page && <Icon name="arrowUpRight" size={14} />}
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="set-menu__none">{say('admin.findNone', { q })}</p>
        )
      ) : (
        <Accordion className="set-menu__areas" allowsMultipleExpanded hideSeparator expandedKeys={open} onExpandedChange={setOpen}>
          {groups.map((g) => (
            <Accordion.Item key={g.key} id={g.key} className="set-menu__area">
              <Accordion.Heading>
                <Accordion.Trigger className="set-menu__name">
                  {word(g.label)}
                  <Accordion.Indicator />
                </Accordion.Trigger>
              </Accordion.Heading>
              <Accordion.Panel>
                <ul className="set-menu__list">
                  {g.entries.map((e, i) => (
                    <li key={e.key}>
                      {e.part && e.part !== g.entries[i - 1]?.part && <span className="set-menu__part">{word(e.part)}</span>}
                      <button
                        type="button"
                        className="set-menu__item"
                        aria-current={!e.page && e.key === currentKey ? 'page' : undefined}
                        onClick={() => onOpen(e)}
                      >
                        <Icon name={e.icon} size={16} />
                        <span>{word(e.label)}</span>
                        {e.page && <Icon name="arrowUpRight" size={14} />}
                      </button>
                    </li>
                  ))}
                </ul>
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      )}
    </nav>
  )
}
